/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {BlockNavigationOptions, LocationLike, PageApi, ShellRedirectOptions} from '@adobe/exc-app/page';
import {Events} from '@exc/shared';
import {ExternalConfiguration} from '../ExternalConfiguration';
import {generateShellUrl} from '../actions';
import {getPerformanceEntries} from '../util';
import {messenger} from '../Messenger';
import Metrics from '../metrics';
import {setupConfig} from '../config';

let configuration: ExternalConfiguration;

const metrics = Metrics.create('exc.module-runtime');

export function addTakeover(): void {
  if (document.getElementById('shell-placeholder')) {
    return;
  }
  const div = document.createElement('div');
  div.id = 'shell-placeholder';
  document.body.insertBefore(div, document.body.childNodes[0]);
  messenger.send(Events.CAN_TAKEOVER, {});
}

const page = {
  blockNavigation: (enabled: boolean, options?: BlockNavigationOptions): void => {
    messenger.send(Events.BLOCK_NAVIGATION, {enabled, options});
  },
  done: () => {
    const iframePerformance = JSON.stringify(getPerformanceEntries());
    const mainElementCount = document.querySelectorAll('main, [role="main"]').length;
    if (mainElementCount !== 1) {
      // eslint-disable-next-line no-console
      console.warn(`Detected ${mainElementCount} main elements on the page. Please ensure there is exactly one main element.`);
    }
    metrics.event(['exc.metrics.pageState.load.done', 'Runtime.done']);
    messenger.send(Events.DONE, {addMainElement: mainElementCount === 0, iframePerformance});

    window.requestAnimationFrame(() => {
      let hasRSP = !!document.querySelector('.react-spectrum-provider');
      metrics.event('rsp.detected', hasRSP);
    });
  },
  generateShellUrl: (location: LocationLike, newApp?: boolean) => {
    return generateShellUrl(configuration, location, newApp);
  },
  iframeReload: (): void => {
    metrics.event('Runtime.iframeReload');
    messenger.send(Events.IFRAME_RELOAD, {});
  },
  notFound: (): void => {
    messenger.send(Events.NOT_FOUND, {});
  },
  openInNewTab: (path: string, newApp?: boolean) => {
    window.open(page.generateShellUrl({path}, newApp));
  },
  shellRedirect: (path, options?: ShellRedirectOptions) => messenger.send(Events.SHELL_REDIRECT, {...options, path})
} as PageApi;

export function onConfigure(config: ExternalConfiguration): void {
  configuration = config;
}

setupConfig(
  page,
  [
    'appContainer',
    'favicon',
    'modal',
    'preventDefaultCombos',
    'spinner',
    'title',
    'unloadPromptMessage',
    'viewportTakeover'
  ],
  (apiName, value) => {
    if (value && (apiName === 'viewportTakeover' || apiName === 'appContainer')) {
      addTakeover();
    }
  });

export default page;
